import {
  getStoreId,
  storeInformationQueries,
} from "$/hooks/use-store-information"
import bookableServiceSlotsOverridesQueries from "$/lib/queries/bookable-service-slots-overrides-queries"
import bookableServiceSlotsQueries from "$/lib/queries/bookable-service-slots-queries"
import { calendarQueries } from "$/lib/queries/calendar-queries"
import storeQueries from "$/lib/queries/store-queries"
import { JobStatus, JobTrait, STORE_TYPE } from "@repo/common"
import {
  createFileRoute,
  redirect,
  retainSearchParams,
} from "@tanstack/react-router"
import { zodValidator } from "@tanstack/zod-adapter"
import { z } from "zod"
import { getStoredCalendarView } from "./-components/hooks/use-calendar-preferences"
import { calendarFetchRange } from "./-utils/calendar-range"

// Sort options for jobs
export const JobSortOption = {
  PRIORITY: "priority",
  STATUS_BOOKED_TO_DONE: "status_booked_to_done",
  STATUS_DONE_TO_BOOKED: "status_done_to_booked",
  CUSTOMER_NAME_AZ: "customer_name_az",
  RECENTLY_CREATED: "recently_created",
  BIKE_TAG: "bike_tag",
  RECEIPT_PRICE_LOW_HIGH: "receipt_price_low_high",
  RECEIPT_PRICE_HIGH_LOW: "receipt_price_high_low",
} as const

export type JobSortOption = (typeof JobSortOption)[keyof typeof JobSortOption]

export const Route = createFileRoute("/(app)/_auth/calendar")({
  validateSearch: zodValidator(
    z.object({
      view: z.enum(["day", "week", "month"]).optional().catch(undefined),
      startDate: z.string().optional(),
      endDate: z.string().optional(),
      selectedDate: z.string().optional(), // For mobile view - the currently selected day
      status: z
        .array(z.union([z.nativeEnum(JobStatus), z.nativeEnum(JobTrait)]))
        .default([])
        .catch([]),
      searchQuery: z.string().default(""),
      tagSearchQuery: z.string().default(""),
      mechanicId: z.string().optional(),
      companyId: z.string().optional(),
      departmentId: z.string().optional(),
      filterByReception: z.boolean().default(true),
      filterByInspection: z.boolean().default(true),
      filterByAssigned: z.boolean().default(true),
      sortBy: z
        .enum([
          "priority",
          "status_booked_to_done",
          "status_done_to_booked",
          "customer_name_az",
          "recently_created",
          "bike_tag",
          "receipt_price_low_high",
          "receipt_price_high_low",
        ])
        .default("priority"),
      statusFilterMode: z.enum(["include", "exclude"]).default("include"),
      labelIds: z.array(z.string()).default([]),
      labelFilterMode: z.enum(["include", "exclude"]).default("include"),
    }),
  ),
  search: {
    middlewares: [retainSearchParams(true)],
  },
  loaderDeps: ({ search }) => ({
    view: search.view,
    startDate: search.startDate,
  }),
  loader: async ({ deps, context: { queryClient } }) => {
    // Redirect mobile stores to events page
    const storeInfo = await queryClient.ensureQueryData(
      storeInformationQueries.detail(queryClient),
    )
    const currentStore = storeInfo.stores.find(
      (store) => store.id === storeInfo.storeId,
    )
    if (currentStore?.type === STORE_TYPE.MOBILE) {
      throw redirect({
        to: "/event",
        replace: true,
      })
    }

    const storeId = await getStoreId()
    if (!storeId) throw new Error("No store ID found")

    await queryClient.fetchQuery(storeQueries.detail(storeId))

    // Warm the exact grid range + slot config on intent-preload (link hover) so
    // the calendar's blocking queries are already in-flight/resolved when the
    // component mounts — shrinking the loading skeleton. Non-awaited so the
    // loader itself doesn't block on them.
    const view = deps.view ?? getStoredCalendarView() ?? "month"
    const { start, end } = calendarFetchRange(view, deps.startDate, new Date())
    queryClient.prefetchQuery(calendarQueries.betweenDates(storeId, start, end))
    queryClient.prefetchQuery(bookableServiceSlotsQueries.all(storeId))
    queryClient.prefetchQuery(bookableServiceSlotsOverridesQueries.all(storeId))

    return {
      crumb: "alfred.pages.calendar.crumbs.calendar",
    }
  },
})
